import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getSchemaSearchableMenuOptionDatas} from 'samples/SchemaStarsUtil';
import {BaseStarsAwareSchemaSelectorProps} from 'samples/StarsAwareSchemaSelector';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import MultiSelectSearchableDropdown, {
  MultiSelectSearchableDropdownProps,
} from 'ui-library/dropdowns/MultiSelectSearchableDropdown';
import {getRenderStarredOptionContent} from 'util/getRenderStarredOptionContent';
import {BasicEvent} from 'util/Types';

export type MultiSelectStarsAwareSchemaSelectorProps = Omit<
  MultiSelectSearchableDropdownProps,
  'optionDatas' | 'selectedValues' | 'onSelect' | 'noneSelectedMessage' | 'disabled'
> &
  BaseStarsAwareSchemaSelectorProps & {
    schemas: Array<{id: string; name: string}> | null;
    selectedSchemaIds: Array<string>;
    onSelectSchemaIds: (schemaIds: Array<string>) => void;
    isDisabled?: boolean;
  };

export default function MultiSelectStarsAwareSchemaSelector(
  props: MultiSelectStarsAwareSchemaSelectorProps
): JSX.Element {
  const dataTestAttrs = {component: 'SchemaSelector', element: 'starredOptionContent'};

  // Does not currently support schema interfaces; those would need to be queried for and passed in here
  const menuOptionDatas = getSchemaSearchableMenuOptionDatas(props.schemas, props.starredSchemaIds, {
    isSchemaDisabledFn: props.isSchemaDisabledFn,
    disabledMessage: props.disabledMessage,
    sections: props.sections,
    getSchemaOwner: props.getSchemaOwner,
    transformSchema: (schema) => schema.id,
    optionIcon: 'check',
  });

  const renderChips = (
    selectedSchemaIds: Array<string>,
    deselectValueAtIndex: (idx: number) => void
  ): JSX.Element => (
    <>
      {selectedSchemaIds
        .filter((selectedSchemaId) => !selectedSchemaId.startsWith('any_'))
        .map((selectedSchemaId, index) => (
          <BenchChip
            key={index}
            className="chip-container"
            size={ChipSize.SMALL}
            id={selectedSchemaId}
            onRemove={
              props.isDisabled
                ? null
                : (e: BasicEvent) => {
                    deselectValueAtIndex(index);
                  }
            }
          />
        ))}
    </>
  );
  return (
    <MultiSelectSearchableDropdown
      {...props}
      disabled={!props.schemas || Boolean(props.isDisabled)}
      noneSelectedMessage="Select schemas"
      optionDatas={menuOptionDatas}
      selectedValues={props.selectedSchemaIds}
      onSelect={(schemaIds) => {
        props.onSelectSchemaIds(schemaIds);
      }}
      renderSelectedValues={renderChips}
      menuProps={{
        renderOptionContent: getRenderStarredOptionContent(
          props.onToggleStar,
          props.starredSchemaIds,
          {getId: (optionData) => optionData.value},
          css(styles.optionItem),
          dataTestAttrs
        ),
        itemContainerClassName: css(styles.optionItemContainer),
      }}
    />
  );
}

const styles = StyleSheet.create({
  optionItem: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  optionItemContainer: {
    minHeight: 20,
    height: 'auto',
  },
});
